import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  Alert,
  Skeleton,
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import {
  Add,
  Business,
  CardMembership,
  PauseCircle,
  PlayArrow,
  WarningAmber,
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import useCheckPermission from '../../hooks/useCheckPermission';
import tenantService from '../tenants/tenantService';
import tenantOnboardingService from '../tenants/tenantOnboardingService';
import subscriptionService from '../subscriptions/subscriptionService';
import platformUsageService, {
  formatBytes,
  formatCount,
  normalizeOperatorAlerts,
  readContentInventory,
  severityColor,
} from './platformUsageService';
import type { OperatorAlert } from './platformUsageService';
import { TenantStatus } from '../tenants/TenantForm';
import {
  readStoredAdminMode,
  userHasPlatformSaaSAccess,
  writeStoredAdminMode,
} from '../../utils/adminMode';
import UsagePeriodPicker from '../../components/saas/UsagePeriodPicker';
import {
  UsagePeriodValue,
  createDefaultUsagePeriod,
  formatPeriodLabel,
} from '../../components/saas/usagePeriod';

const DRAFT_STATUS_IN_PROGRESS = 0;

const SubscriptionStatus = {
  Active: 1,
  Inactive: 2,
  Suspended: 3,
  Deleted: 4,
  Trialing: 5,
  PastDue: 6,
  Canceled: 7,
} as const;

const asDate = (value: any) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDate = (value: any) => {
  const d = asDate(value);
  if (!d) return '—';
  return d.toLocaleDateString('hu-HU');
};

const StatCard = ({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: number | string;
  hint?: string;
  tone?: 'default' | 'warn' | 'ok';
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      height: '100%',
      borderColor:
        tone === 'warn'
          ? 'warning.main'
          : tone === 'ok'
            ? 'success.main'
            : 'divider',
      bgcolor: 'background.paper',
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h4" sx={{ mt: 0.5, lineHeight: 1.1, fontWeight: 700 }}>
      {value}
    </Typography>
    {hint && (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 0.75, display: 'block' }}
      >
        {hint}
      </Typography>
    )}
  </Paper>
);

const CHART_COLORS = [
  '#0B6E4F',
  '#1B9AAA',
  '#EF8354',
  '#4F6D7A',
  '#F4A261',
  '#2A9D8F',
  '#E76F51',
  '#264653',
  '#8AB17D',
  '#BC6C25',
  '#3D5A80',
  '#EE6C4D',
  '#293241',
];

const TenantSharePie = ({
  title,
  subtitle,
  slices,
  valueFormatter,
  onSliceClick,
}: {
  title: string;
  subtitle?: string;
  slices: { id: string; label: string; value: number }[];
  valueFormatter: (value: number) => string;
  onSliceClick?: (id: string) => void;
}) => {
  const data = slices
    .filter((s) => s.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 8)
    .map((s, i) => ({
      id: s.id,
      label: s.label,
      value: s.value,
      color: CHART_COLORS[i % CHART_COLORS.length],
    }));

  const total = data.reduce((sum, s) => sum + s.value, 0);

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 1 }}
        >
          {subtitle}
        </Typography>
      )}
      {total <= 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 6, textAlign: 'center' }}
        >
          Nincs megjeleníthető adat
        </Typography>
      ) : (
        <PieChart
          height={260}
          series={[
            {
              data,
              innerRadius: 48,
              outerRadius: 95,
              paddingAngle: 1.5,
              cornerRadius: 3,
              arcLabel: (item) => {
                const pct = total > 0 ? (item.value / total) * 100 : 0;
                return pct >= 8 ? `${Math.round(pct)}%` : '';
              },
              arcLabelMinAngle: 20,
              valueFormatter: (item) => valueFormatter(Number(item.value) || 0),
              highlightScope: { fade: 'global', highlight: 'item' },
            },
          ]}
          onItemClick={(_event, itemIdentifier) => {
            const id = data[itemIdentifier.dataIndex]?.id;
            if (id && onSliceClick) onSliceClick(id);
          }}
          slotProps={{
            legend: {
              direction: 'horizontal',
              position: { vertical: 'bottom', horizontal: 'center' },
            },
          }}
          sx={{
            cursor: onSliceClick ? 'pointer' : 'default',
            '& text': { fontSize: 11, fontWeight: 700 },
          }}
        />
      )}
      {total > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}
        >
          Összesen: {valueFormatter(total)}
        </Typography>
      )}
    </Paper>
  );
};

const mergeUsageTenants = (usage: any) => {
  const map = new Map<string, any>();
  const lists = [
    usage?.topByContent,
    usage?.topByActiveDevices,
    usage?.topByApiCalls,
    usage?.topByStorage,
  ];
  lists.forEach((list) => {
    (list ?? []).forEach((row: any) => {
      const id = String(row.tenantId ?? row.TenantId ?? '');
      if (!id) return;
      const prev = map.get(id) ?? {};
      map.set(id, {
        ...prev,
        ...row,
        tenantId: id,
        tenantName: row.tenantName ?? row.TenantName ?? prev.tenantName ?? 'Bérlő',
        contentTotal: Number(
          row.contentTotal ?? row.ContentTotal ?? prev.contentTotal ?? 0
        ),
        articles: Number(row.articles ?? row.Articles ?? prev.articles ?? 0),
        installedDevices: Number(
          row.installedDevices ??
            row.InstalledDevices ??
            prev.installedDevices ??
            0
        ),
        activeDevices30d: Number(
          row.activeDevices30d ??
            row.ActiveDevices30d ??
            prev.activeDevices30d ??
            0
        ),
        maxDevices: Number(
          row.maxDevices ?? row.MaxDevices ?? prev.maxDevices ?? 0
        ),
        apiCallsMobile: Number(
          row.apiCallsMobile ?? row.ApiCallsMobile ?? prev.apiCallsMobile ?? 0
        ),
        storageBytes: Number(
          row.storageBytes ?? row.StorageBytes ?? prev.storageBytes ?? 0
        ),
      });
    });
  });

  return [...map.values()].sort((a, b) => {
    const score = (t: any) =>
      Number(t.contentTotal) * 10 +
      Number(t.installedDevices) +
      Number(t.apiCallsMobile) +
      Number(t.storageBytes) / 1_000_000;
    return score(b) - score(a);
  });
};

const PlatformDashboardPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { enqueueSnackbar } = useSnackbar();
  const { canPlatform } = useCheckPermission();
  const platformPermissions = useSelector(
    (state: any) => state.user.userInfo?.platformPermissions
  );
  const multiTenancyEnabled = useSelector(
    (state: any) => state.app.featureFlags?.multiTenancyEnabled
  );

  const canTenants = canPlatform(['ManageTenants']);
  const canSubscriptions = canPlatform(['ManageSubscriptions']);
  const isPlatformAdmin = userHasPlatformSaaSAccess(platformPermissions);

  const [tenants, setTenants] = useState<any[]>([]);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [drafts, setDrafts] = useState<any[]>([]);
  const [usage, setUsage] = useState<any>(null);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usagePeriod, setUsagePeriod] = useState<UsagePeriodValue>(() =>
    createDefaultUsagePeriod()
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!multiTenancyEnabled || !isPlatformAdmin) {
      navigate('/', { replace: true });
      return;
    }
    // AppRoutes remounts on adminMode change while URL may still be /platform.
    // Do not override an in-flight tenant switch (stored mode already 'tenant').
    if (readStoredAdminMode() === 'tenant') {
      return;
    }
    writeStoredAdminMode('saas');
    dispatch({ type: 'SET_ADMIN_MODE', payload: 'saas' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally ignore adminMode
  }, [multiTenancyEnabled, isPlatformAdmin, dispatch, navigate]);

  useEffect(() => {
    if (!multiTenancyEnabled || !isPlatformAdmin) {
      return;
    }

    setLoading(true);
    dispatch({ type: 'SHOW_QUERY' });

    const tasks: Promise<void>[] = [];

    if (canTenants) {
      tasks.push(
        tenantService.list().then((response) => {
          if (!response.hasError) setTenants(response.records ?? []);
          else
            enqueueSnackbar(response.errorMessages?.join(',') || 'Hiba', {
              variant: 'error',
            });
        }),
        tenantOnboardingService.list().then((response) => {
          if (!response.hasError) {
            setDrafts(
              (response.records ?? []).filter(
                (d: any) =>
                  (d.status ?? d.Status) === DRAFT_STATUS_IN_PROGRESS
              )
            );
          }
        })
      );
    }

    if (canSubscriptions) {
      tasks.push(
        subscriptionService.list().then((response) => {
          if (!response.hasError) setSubscriptions(response.records ?? []);
          else
            enqueueSnackbar(response.errorMessages?.join(',') || 'Hiba', {
              variant: 'error',
            });
        })
      );
    }

    Promise.all(tasks)
      .catch(() => {
        enqueueSnackbar('Nem sikerült betölteni a platform áttekintést.', {
          variant: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
        dispatch({ type: 'HIDE' });
      });
  }, [
    multiTenancyEnabled,
    isPlatformAdmin,
    canTenants,
    canSubscriptions,
    dispatch,
    enqueueSnackbar,
  ]);

  const loadUsage = useCallback(
    (period: UsagePeriodValue) => {
      if (!canTenants || !multiTenancyEnabled || !isPlatformAdmin) return;
      setUsageLoading(true);
      platformUsageService
        .getSummary({
          from: period.from,
          to: period.to,
          top: 20,
        })
        .then((response) => {
          if (response.hasError) {
            setUsage(null);
            setUsageError(
              (response.errorMessages ?? []).join(', ') ||
                'Használati adatok nem elérhetők'
            );
          } else {
            setUsageError(null);
            setUsage(response.result);
          }
        })
        .finally(() => setUsageLoading(false));
    },
    [canTenants, multiTenancyEnabled, isPlatformAdmin]
  );

  useEffect(() => {
    loadUsage(usagePeriod);
  }, [loadUsage, usagePeriod]);

  const stats = useMemo(() => {
    const activeTenants = tenants.filter(
      (t) => (t.status ?? t.Status) === TenantStatus.Active
    ).length;
    const suspendedTenants = tenants.filter(
      (t) => (t.status ?? t.Status) === TenantStatus.Suspended
    ).length;
    const pastDue = subscriptions.filter(
      (s) => (s.status ?? s.Status) === SubscriptionStatus.PastDue
    ).length;
    const trialing = subscriptions.filter(
      (s) => (s.status ?? s.Status) === SubscriptionStatus.Trialing
    ).length;
    return {
      tenantCount: tenants.length,
      activeTenants,
      suspendedTenants,
      pastDue,
      trialing,
      draftCount: drafts.length,
    };
  }, [tenants, subscriptions, drafts]);

  const operatorAlerts = useMemo(
    () => normalizeOperatorAlerts(usage),
    [usage]
  );

  const attentionItems = useMemo(() => {
    const items: {
      id: string;
      title: string;
      detail: string;
      to: string;
      tone?: 'warn' | 'error';
    }[] = [];

    tenants
      .filter((t) => (t.status ?? t.Status) === TenantStatus.Suspended)
      .slice(0, 8)
      .forEach((t) => {
        items.push({
          id: `sus-${t.id}`,
          title: t.name ?? t.Name ?? 'Bérlő',
          detail: 'Felfüggesztett bérlő',
          to: `/tenants/${t.id}/overview`,
          tone: 'warn',
        });
      });

    subscriptions
      .filter((s) => (s.status ?? s.Status) === SubscriptionStatus.PastDue)
      .slice(0, 8)
      .forEach((s) => {
        const tenantName =
          s.tenant?.name ?? s.Tenant?.Name ?? s.tenantName ?? 'Előfizetés';
        items.push({
          id: `pd-${s.id}`,
          title: tenantName,
          detail: `Lejárt díjú előfizetés · türelmi idő: ${formatDate(
            s.graceEndsAt ?? s.GraceEndsAt
          )}`,
          to: `/subscriptions/edit/${s.id}`,
          tone: 'warn',
        });
      });

    operatorAlerts.forEach((alert: OperatorAlert) => {
      items.push({
        id: `max-${alert.kind}-${alert.tenantId}`,
        title: alert.tenantName || 'Bérlő',
        detail: alert.message,
        to: `/tenants/${alert.tenantId}/overview`,
        tone: alert.severity === 'critical' ? 'error' : 'warn',
      });
    });

    return items.slice(0, 12);
  }, [tenants, subscriptions, operatorAlerts]);

  const recentTenants = useMemo(() => {
    return [...tenants]
      .sort((a, b) => {
        const da = asDate(a.createdAt ?? a.CreatedAt)?.getTime() ?? 0;
        const db = asDate(b.createdAt ?? b.CreatedAt)?.getTime() ?? 0;
        return db - da;
      })
      .slice(0, 6);
  }, [tenants]);

  if (!multiTenancyEnabled || !isPlatformAdmin) {
    return null;
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{
          mb: 3,
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Platform adminisztráció
          </Typography>
          <Typography variant="body2" color="text.secondary">
            SaaS áttekintés — bérlők, előfizetések és folyamatban lévő
            onboarding.
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ flexWrap: 'wrap' }}
        >
          {canTenants && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/tenants/new')}
            >
              Új bérlő
            </Button>
          )}
          {canTenants && (
            <Button
              variant="outlined"
              startIcon={<Business />}
              onClick={() => navigate('/tenants')}
            >
              Bérlők
            </Button>
          )}
          {canSubscriptions && (
            <Button
              variant="outlined"
              startIcon={<CardMembership />}
              onClick={() => navigate('/subscriptions')}
            >
              Előfizetések
            </Button>
          )}
        </Stack>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {canTenants && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label="Bérlők"
                value={loading ? '…' : stats.tenantCount}
                hint={`${stats.activeTenants} aktív`}
                tone="ok"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label="Felfüggesztett"
                value={loading ? '…' : stats.suspendedTenants}
                tone={stats.suspendedTenants > 0 ? 'warn' : 'default'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label="Folyamatban lévő onboarding"
                value={loading ? '…' : stats.draftCount}
                hint={
                  stats.draftCount > 0
                    ? 'Folytatható piszkozatok'
                    : undefined
                }
              />
            </Grid>
          </>
        )}
        {canSubscriptions && (
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              label="Figyelendő előfizetés"
              value={loading ? '…' : stats.pastDue}
              hint={`${stats.trialing} próbaidős`}
              tone={stats.pastDue > 0 ? 'warn' : 'default'}
            />
          </Grid>
        )}
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 1.5, alignItems: 'center' }}
            >
              <WarningAmber color="warning" fontSize="small" />
              <Typography sx={{ fontWeight: 700 }}>
                Figyelmet igényel
              </Typography>
            </Stack>
            {attentionItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {loading
                  ? 'Betöltés…'
                  : 'Nincs azonnali teendő a bérlők és előfizetések között.'}
              </Typography>
            ) : (
              <Stack divider={<Divider flexItem />} spacing={0}>
                {attentionItems.map((item) => (
                  <Box
                    key={item.id}
                    onClick={() => navigate(item.to)}
                    sx={{
                      py: 1.25,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      px: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontWeight: 600 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.detail}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        color={item.tone === 'error' ? 'error' : 'warning'}
                        label={item.tone === 'error' ? 'Kritikus' : 'Figyelem'}
                        sx={{ height: 22 }}
                      />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography sx={{ mb: 1.5, fontWeight: 700 }}>
              Legutóbbi bérlők
            </Typography>
            {!canTenants ? (
              <Typography variant="body2" color="text.secondary">
                Nincs jogosultság a bérlők megtekintéséhez.
              </Typography>
            ) : recentTenants.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {loading ? 'Betöltés…' : 'Még nincs bérlő.'}
              </Typography>
            ) : (
              <Stack divider={<Divider flexItem />} spacing={0}>
                {recentTenants.map((t) => {
                  const status = t.status ?? t.Status;
                  const suspended = status === TenantStatus.Suspended;
                  return (
                    <Box
                      key={t.id}
                      onClick={() => navigate(`/tenants/${t.id}/overview`)}
                      sx={{
                        py: 1.25,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        px: 0.5,
                        borderRadius: 1,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap sx={{ fontWeight: 600 }}>
                            {t.name ?? t.Name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            Létrehozva:{' '}
                            {formatDate(t.createdAt ?? t.CreatedAt)}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          icon={
                            suspended ? (
                              <PauseCircle sx={{ fontSize: 16 }} />
                            ) : undefined
                          }
                          label={suspended ? 'Felfüggesztett' : 'Aktív'}
                          color={suspended ? 'warning' : 'success'}
                          variant="outlined"
                          sx={{ height: 22 }}
                        />
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Grid>

        {canTenants && drafts.length > 0 && (
          <Grid size={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography sx={{ mb: 1.5, fontWeight: 700 }}>
                Folyamatban lévő onboarding
              </Typography>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                useFlexGap
                sx={{ flexWrap: 'wrap' }}
              >
                {drafts.slice(0, 8).map((d) => {
                  const draftId = d.id ?? d.Id;
                  const name =
                    d.payload?.tenant?.name?.trim() || 'Névtelen piszkozat';
                  return (
                    <Button
                      key={draftId}
                      variant="outlined"
                      startIcon={<PlayArrow />}
                      onClick={() => navigate(`/tenants/new/${draftId}`)}
                    >
                      {name}
                    </Button>
                  );
                })}
              </Stack>
            </Paper>
          </Grid>
        )}

        {canTenants && (
          <Grid size={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{
                  mb: 2,
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'flex-start' },
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>Használat</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Platform összesítés: minden bérlő adatai összeadva · kördiagramok
                    a bérlők / tartalomtípusok arányáról ·{' '}
                    {formatPeriodLabel(usagePeriod)}
                  </Typography>
                </Box>
                {operatorAlerts.length > 0 && (
                  <Chip
                    size="small"
                    color={
                      operatorAlerts.some((a) => a.severity === 'critical')
                        ? 'error'
                        : 'warning'
                    }
                    label={`${operatorAlerts.length} max riasztás`}
                  />
                )}
              </Stack>

              <Box sx={{ mb: 2 }}>
                <UsagePeriodPicker
                  value={usagePeriod}
                  onChange={setUsagePeriod}
                  disabled={usageLoading}
                />
              </Box>

              {(loading || usageLoading) && !usage && !usageError && (
                <Grid container spacing={2}>
                  {[0, 1, 2, 3].map((i) => (
                    <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
                      <Skeleton variant="rounded" height={88} />
                    </Grid>
                  ))}
                </Grid>
              )}

              {usageError && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  {usageError}
                </Alert>
              )}

              {!loading && !usage && !usageError && (
                <Typography variant="body2" color="text.secondary">
                  Még nincs használati adat. A mobil API forgalom és a napi
                  összesítők megjelenése után töltődik.
                </Typography>
              )}

              {usage && (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <StatCard
                        label="Aktív eszköz"
                        value={formatCount(
                          usage.portfolio?.activeDevices30d ??
                            usage.portfolio?.ActiveDevices30d
                        )}
                        hint={`Gördülő 30 nap · ${formatCount(
                          usage.portfolio?.installedDevices ??
                            usage.portfolio?.InstalledDevices
                        )} telepített`}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <StatCard
                        label="Mobil API hívás"
                        value={formatCount(
                          usage.portfolio?.apiCallsMobile ??
                            usage.portfolio?.ApiCallsMobile
                        )}
                        hint={
                          Number(
                            usage.portfolio?.apiCallsCms ??
                              usage.portfolio?.ApiCallsCms
                          ) > 0
                            ? `Összes bérlő · CMS: ${formatCount(
                                usage.portfolio?.apiCallsCms ??
                                  usage.portfolio?.ApiCallsCms
                              )}`
                            : 'Összes bérlő összege a kiválasztott időszakban'
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <StatCard
                        label="Tárhely"
                        value={formatBytes(
                          usage.portfolio?.storageBytes ??
                            usage.portfolio?.StorageBytes
                        )}
                        hint="Összes bérlő · létező blobok mérete"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <StatCard
                        label="Mobilapp tartalom"
                        value={formatCount(
                          usage.portfolio?.contentTotal ??
                            usage.portfolio?.ContentTotal
                        )}
                        hint={`Összes bérlő · cikkek: ${formatCount(
                          usage.portfolio?.articles ?? usage.portfolio?.Articles
                        )}`}
                      />
                    </Grid>
                  </Grid>

                  {(() => {
                    const tenants = mergeUsageTenants(usage);
                    const inventoryRows = readContentInventory(usage).filter(
                      (x) => x.count > 0
                    );
                    const goTenant = (id: string) =>
                      navigate(`/tenants/${id}/overview`);

                    return (
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TenantSharePie
                            title="Tartalomtípusok"
                            subtitle="Platform-szintű darabszámok aránya"
                            slices={inventoryRows.map((row) => ({
                              id: row.key,
                              label: row.label,
                              value: row.count,
                            }))}
                            valueFormatter={formatCount}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TenantSharePie
                            title="Tartalom bérlőnként"
                            subtitle="Melyik bérlő adja a tartalom mekkora részét"
                            slices={tenants.map((t) => ({
                              id: t.tenantId,
                              label: t.tenantName,
                              value: Number(t.contentTotal) || 0,
                            }))}
                            valueFormatter={formatCount}
                            onSliceClick={goTenant}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TenantSharePie
                            title="Tárhely bérlőnként"
                            subtitle="Blobstorage foglalás megoszlása"
                            slices={tenants.map((t) => ({
                              id: t.tenantId,
                              label: t.tenantName,
                              value: Number(t.storageBytes) || 0,
                            }))}
                            valueFormatter={formatBytes}
                            onSliceClick={goTenant}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TenantSharePie
                            title="Telepített eszközök bérlőnként"
                            subtitle="FCM tokenek aránya a platformon"
                            slices={tenants.map((t) => ({
                              id: t.tenantId,
                              label: t.tenantName,
                              value: Number(t.installedDevices) || 0,
                            }))}
                            valueFormatter={formatCount}
                            onSliceClick={goTenant}
                          />
                        </Grid>
                        {tenants.some(
                          (t) =>
                            Number(t.apiCallsMobile) > 0 ||
                            Number(t.maxDevices) > 0
                        ) && (
                          <>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <TenantSharePie
                                title="Mobil API bérlőnként"
                                subtitle="Időszaki hívások megoszlása"
                                slices={tenants.map((t) => ({
                                  id: t.tenantId,
                                  label: t.tenantName,
                                  value: Number(t.apiCallsMobile) || 0,
                                }))}
                                valueFormatter={formatCount}
                                onSliceClick={goTenant}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <TenantSharePie
                                title="MaxDevices kihasználtság"
                                subtitle="Licencelt limitből felhasznált eszközök bérlőnként"
                                slices={tenants
                                  .filter((t) => Number(t.maxDevices) > 0)
                                  .map((t) => ({
                                    id: t.tenantId,
                                    label: `${t.tenantName} (${formatCount(
                                      Math.max(
                                        Number(t.installedDevices) || 0,
                                        Number(t.activeDevices30d) || 0
                                      )
                                    )}/${formatCount(Number(t.maxDevices) || 0)})`,
                                    value: Math.max(
                                      Number(t.installedDevices) || 0,
                                      Number(t.activeDevices30d) || 0
                                    ),
                                  }))}
                                valueFormatter={formatCount}
                                onSliceClick={goTenant}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    );
                  })()}

                  {operatorAlerts.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        MaxDevices riasztások
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1 }}
                      >
                        Üzemeltetői figyelmeztetés a licenc MaxDevices értékhez —
                        nem blokkolja a szolgáltatást.
                      </Typography>
                      <Stack divider={<Divider flexItem />} spacing={0}>
                        {operatorAlerts.slice(0, 8).map((row) => (
                          <Box
                            key={`${row.kind}-${row.tenantId}`}
                            onClick={() =>
                              navigate(`/tenants/${row.tenantId}/overview`)
                            }
                            sx={{
                              py: 1,
                              px: 0.5,
                              cursor: 'pointer',
                              borderRadius: 1,
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography noWrap sx={{ fontWeight: 600 }}>
                                  {row.tenantName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {row.message}
                                </Typography>
                              </Box>
                              <Chip
                                size="small"
                                color={severityColor(row.severity)}
                                label={
                                  row.severity === 'critical'
                                    ? 'Túllépve'
                                    : 'Közel'
                                }
                                sx={{ height: 22, flexShrink: 0 }}
                              />
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default PlatformDashboardPage;
